import { useTranslation } from "react-i18next";

function ToggleSwitch({ checked, onChange, disabled, testId }) {
  return (
    <button type="button" role="switch" aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)} disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-slate-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid={testId}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

export function AdvancedOptionsSection({ consulta, editing, form, onFieldChange }) {
  const { t } = useTranslation();

  // Unified incompatibility handlers
  const handleVotMaAlcada = (val) => {
    onFieldChange('enableVotMaAlcada', val);
    if (val) { onFieldChange('enableAudit', false); onFieldChange('enablePrivateAudit', false); onFieldChange('enableVoterTracking', false); }
  };
  const handleAudit = (val) => {
    onFieldChange('enableAudit', val);
    if (val) { onFieldChange('enableVotMaAlcada', false); onFieldChange('enablePrivateAudit', false); }
  };
  const handlePrivateAudit = (val) => {
    onFieldChange('enablePrivateAudit', val);
    if (val) { onFieldChange('enableVotMaAlcada', false); onFieldChange('enableAudit', false); }
  };
  const handleVoterTracking = (val) => {
    onFieldChange('enableVoterTracking', val);
    if (val) { onFieldChange('enableVotMaAlcada', false); }
  };

  const getWarning = (field) => {
    if (field === 'enableVotMaAlcada' && (form.enableAudit || form.enablePrivateAudit || form.enableVoterTracking)) return t('newConsulta.incompatibleWithAudit');
    if (field === 'enableAudit' && form.enableVotMaAlcada) return t('newConsulta.incompatibleWithHandVote');
    if (field === 'enableAudit' && form.enablePrivateAudit) return t('newConsulta.incompatibleWithPrivateAudit');
    if (field === 'enablePrivateAudit' && form.enableVotMaAlcada) return t('newConsulta.incompatibleWithHandVote');
    if (field === 'enablePrivateAudit' && form.enableAudit) return t('newConsulta.incompatibleWithAudit');
    if (field === 'enableVoterTracking' && form.enableVotMaAlcada) return t('newConsulta.incompatibleWithHandVote');
    return null;
  };

  const options = [
    { key: 'requireRegistration', label: t('consultaDetail.requireRegistration'), desc: t('consultaDetail.requireRegistrationDesc'), field: 'requireRegistration' },
    {
      key: 'enablePrivateAudit', label: t('consultaDetail.privateAudit'), desc: t('consultaDetail.privateAuditDesc'), field: 'enablePrivateAudit',
      disabled: form.enableVotMaAlcada || form.enableAudit,
      warning: getWarning('enablePrivateAudit'),
      onToggle: handlePrivateAudit,
    },
    {
      key: 'enableVotMaAlcada', label: t('consultaDetail.handVote'), desc: t('consultaDetail.handVoteDesc'), field: 'enableVotMaAlcada',
      disabled: form.enableAudit || form.enablePrivateAudit || form.enableVoterTracking,
      warning: getWarning('enableVotMaAlcada'),
      onToggle: handleVotMaAlcada,
    },
    {
      key: 'enableAudit', label: t('consultaDetail.audit'), desc: t('consultaDetail.auditDesc'), field: 'enableAudit',
      disabled: form.enableVotMaAlcada || form.enablePrivateAudit,
      warning: getWarning('enableAudit'),
      onToggle: handleAudit,
    },
    {
      key: 'enableVoterTracking', label: t('consultaDetail.participationControl'), desc: t('consultaDetail.participationControlDesc'), field: 'enableVoterTracking',
      disabled: form.enableVotMaAlcada,
      warning: getWarning('enableVoterTracking'),
      onToggle: handleVoterTracking,
    },
    { key: 'enableStatsSex', label: t('consultaDetail.statsBySex'), desc: t('consultaDetail.statsBySexDesc'), field: 'enableStatsSex' },
    { key: 'enableStatsAge', label: t('consultaDetail.statsByAge'), desc: t('consultaDetail.statsByAgeDesc'), field: 'enableStatsAge' },
  ];

  const viewOptions = [
    { label: t('consultaDetail.requireRegistration'), active: consulta?.require_registration },
    { label: t('consultaDetail.privateAudit'), active: consulta?.enable_private_audit },
    { label: t('consultaDetail.handVote'), active: consulta?.enable_vot_ma_alcada },
    { label: t('consultaDetail.audit'), active: consulta?.enable_audit },
    { label: t('consultaDetail.participationControl'), active: consulta?.enable_voter_tracking },
    { label: t('consultaDetail.statsBySex'), active: consulta?.enable_stats_sex !== false },
    { label: t('consultaDetail.statsByAge'), active: consulta?.enable_stats_age !== false },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <h2 className="text-lg font-heading font-semibold text-slate-900 mb-4">{t('consultaDetail.advancedOptions')}</h2>
      {editing ? (
        <div className="space-y-4">
          {options.map(opt => (
            <div key={opt.key} className="flex items-center justify-between">
              <div>
                <p className="font-medium text-slate-900">{opt.label}</p>
                <p className="text-sm text-slate-500">{opt.desc}</p>
                {opt.warning && <p className="text-xs text-amber-600">{opt.warning}</p>}
              </div>
              <ToggleSwitch
                checked={form[opt.field]}
                onChange={opt.onToggle || ((val) => onFieldChange(opt.field, val))}
                disabled={opt.disabled}
                testId={`edit-${opt.field.replace(/([A-Z])/g, '-$1').toLowerCase()}-switch`}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {viewOptions.map(opt => (
            <div key={opt.label} className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${opt.active ? 'bg-green-500' : 'bg-slate-300'}`} />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
